import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../css/AddUnit.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.milstudio.in';

const AddTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tableName, setTableName] = useState('');
  const [roomTypeId, setRoomTypeId] = useState('');
  const [roomTypes, setRoomTypes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    // Fetch room types from API
    const fetchRoomTypes = async () => {
      try {
        const res = await fetch(`${API_URL}/list-room-types.php`);
        if (!res.ok) throw new Error('Network response was not ok');
        const data = await res.json();
        if (data.status && Array.isArray(data.room_types)) {
          setRoomTypes(data.room_types);
        } else {
          showToast('Failed to load room types', 'error');
        }
      } catch (err) {
        showToast('Error fetching room types', 'error');
      }
    };
    fetchRoomTypes();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tableId = urlParams.get('id');
    
    if (tableId) {
      setIsEditMode(true);
      if (location.state && location.state.table) {
        setTableName(location.state.table.tablename);
        setRoomTypeId(location.state.table.room_type_id);
      } else {
        fetchTableData(tableId);
      }
    } else {
      setIsEditMode(false);
      setTableName('');
      setRoomTypeId('');
    }
  }, [location.search, location.state]);

  const fetchTableData = async (tableId) => {
    try {
      const response = await fetch(`${API_URL}get-table.php?id=${tableId}`);
      const data = await response.json();
      if (data.table) {
        setTableName(data.table.tablename);
        setRoomTypeId(data.table.room_type_id);
      }
    } catch (error) {
      showToast('Error loading table data', 'error');
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tableName.trim()) {
      showToast('Please enter a table name', 'error');
      return;
    }
    if (!roomTypeId) {
      showToast('Please select a room type', 'error');
      return;
    }
    setIsSubmitting(true);
    try {
      let apiUrl = `${API_URL}/add-table.php`;
      let requestBody = { tablename: tableName, room_type_id: roomTypeId };

      if (isEditMode) {
        const urlParams = new URLSearchParams(location.search);
        const tableId = urlParams.get('id');
        apiUrl = `${API_URL}edit-table.php`;
        requestBody = {
          table_id: tableId,
          tablename: tableName,
          room_type_id: roomTypeId
        };
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      const data = await response.json();
      if (data.status) {
        showToast(`Table "${tableName}" ${isEditMode ? 'updated' : 'added'} successfully!`, 'success');
        if (!isEditMode) {
          setTableName('');
          setRoomTypeId('');
        }
        setTimeout(() => {
          navigate('/table-list');
        }, 1500);
      } else {
        showToast(data.message || `Error ${isEditMode ? 'updating' : 'adding'} table. Please try again.`, 'error');
      }
    } catch (error) {
      showToast(`Error ${isEditMode ? 'updating' : 'adding'} table. Please try again.`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-table-content">
      <div className="page-header" style={{display: 'block'}}>
        <h1>{isEditMode ? 'Edit Table' : 'Add Table'}</h1>
        <p>{isEditMode ? 'Update table information' : 'Add a new table to your restaurant'}</p>
      </div>
      <div className="form-container">
        <form onSubmit={handleSubmit} className="add-unit-form">
          <div className="form-group">
            <label htmlFor="tableName">Table Name</label>
            <input
              type="text"
              id="tableName"
              name="tableName"
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              placeholder="Enter table name (e.g., 1, 2, VIP-1)"
              required
              disabled={isSubmitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="roomType">Room Type</label>
            <select
              id="roomType"
              name="roomType"
              value={roomTypeId}
              onChange={(e) => setRoomTypeId(e.target.value)}
              required
              disabled={isSubmitting}
            >
              <option value="">Select room type</option>
              {roomTypes.map((roomType) => (
                <option key={roomType.id} value={roomType.id}>{roomType.type}</option>
              ))}
            </select>
          </div>
          <button 
            type="submit" 
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? (isEditMode ? 'Updating Table...' : 'Adding Table...') : (isEditMode ? 'Update Table' : 'Add Table')}
          </button>
        </form>
      </div>
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button 
              className="toast-close" 
              onClick={() => setToast({ show: false, message: '', type: '' })}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddTable; 